// src/features/planning/pages/MasterScheduleDetailPage.tsx
// ADR-092 Rule PLN-FE-02. Mirrors BomFamilyDetailPage (ADR-082): family
// details + a sub-table of its revisions, with a link to create a new
// draft revision.
//
// ⚠️ Hook names inferred from ADR-082 pattern — verify (Rule PLN-FE-06).

import { Link, useParams } from "react-router-dom";
import {
  useV1PlanningMasterSchedulesRetrieve,
  useV1PlanningMasterScheduleRevisionsList,
} from "@/api/generated/v1/v1";
import { RevisionStatusBadge } from "../components/RevisionStatusBadge";
import { usePermission } from "@/auth/usePermission";
import type { MasterScheduleRevision } from "../types";
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { extractData, extractResults } from "@/shared/utils/apiResponseUtils";
import { PageBanner } from "@/shared/components/PageBanner";

export function MasterScheduleDetailPage() {
  const { familyId } = useParams<{ familyId: string }>();
  const canManage = usePermission("planning.manage");

  const { data: familyResp, isLoading: familyLoading } =
    useV1PlanningMasterSchedulesRetrieve(familyId!);
  const { data: revisionsData, isLoading: revisionsLoading } =
    useV1PlanningMasterScheduleRevisionsList({ family: familyId });

  const family = extractData<any>(familyResp);
  const revisions = extractResults<MasterScheduleRevision>(revisionsData);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      return new DateObject({ date: dateString, format: "YYYY-MM-DD" })
        .convert(persian, persian_fa)
        .format("YYYY/MM/DD");
    } catch {
      return dateString;
    }
  };

  if (familyLoading) return <p>در حال بارگذاری...</p>;
  if (!family) return <p>برنامه یافت نشد.</p>;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`${family?.code} — ${family?.name}`}
        subtitle="جزئیات برنامه کلان تولید و مدیریت نسخه‌ها (Revisions)"
        icon="🗓️"
        helpLink="/help/planning/master-schedule-detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canManage && (
            <Link to="/planning/master-schedules/new" style={{ textDecoration: "none" }}>
              <button
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>➕</span>
                <span>تجدیدنظر (Revision) جدید</span>
              </button>
            </Link>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "1rem" }}>
          لیست نسخه‌ها (Revisions)
        </h2>

        {revisionsLoading ? (
          <p className="text-muted">در حال بارگذاری...</p>
        ) : (
          <table className="w-full text-sm text-left rtl:text-right border-collapse">
            <thead>
              <tr>
                <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">نسخه</th>
                <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">وضعیت</th>
                <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">بازه</th>
                <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10"></th>
              </tr>
            </thead>
            <tbody>
              {revisions.map((rev) => (
                <tr key={rev.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-3 py-2.5">{rev.revision_no}</td>
                  <td className="px-3 py-2.5">
                    <RevisionStatusBadge status={rev.status as any} />
                  </td>
                  <td className="px-3 py-2.5">
                    {formatDate(rev.horizon_start)} تا {formatDate(rev.horizon_end)}
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/planning/revisions/${rev.id}`} className="text-primary-500 hover:underline">مشاهده</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && (
          <p style={{ marginTop: "1rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            <em>
              برای ایجاد نسخهٔ جدید، از صفحهٔ «ایجاد برنامهٔ جدید» با همان
              کد استفاده کنید — شمارهٔ نسخه به‌طور خودکار افزایش می‌یابد.

            </em>
          </p>
        )}
      </div>
    </div>
  );
}
